import styled, { keyframes, css } from "styled-components";
import { tokens, surface, hairline } from "../../styles/designTokens";

const meshDrift = keyframes`
  0%   { transform: translate(0, 0) scale(1); }
  50%  { transform: translate(-20px, 18px) scale(1.05); }
  100% { transform: translate(0, 0) scale(1); }
`;
const pulseDot = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.9; }
  50%      { transform: scale(1.15); opacity: 0.5; }
`;
const fadeInOnly = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const mono = css`
  font-family: ${tokens.fontMono};
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
`;
const eyebrow = css`
  ${mono};
  font-size: ${tokens.fsEyebrow};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.text};
  opacity: 0.45;
`;
const cardBase = (theme) => css`
  background: ${surface(theme, 0)};
  border: 1px solid ${hairline(theme)};
  border-radius: ${tokens.radiusLg};
  transition: border-color ${tokens.durBase} ${tokens.easeOut},
              background ${tokens.durBase} ${tokens.easeOut};
`;

export const Container = styled.div`
  width: 100%;
  max-width: 90em;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 0 2em 2em 2em;
  position: relative;
  min-height: 100vh;
  overflow-y: auto;
  animation: ${fadeInOnly} ${tokens.durSlow} ${tokens.easeOut};

  &::before {
    content: "";
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    background:
      radial-gradient(800px 600px at 15% 10%, ${tokens.brandSoft} 0%, transparent 60%),
      radial-gradient(700px 500px at 85% 70%, rgba(6, 182, 212, 0.05) 0%, transparent 65%);
    animation: ${meshDrift} 24s ${tokens.easeInOut} infinite;
    mask-image: radial-gradient(ellipse at center, black 40%, transparent 90%);
  }

  > * { position: relative; z-index: 1; }

  /* ========== HEADER ========== */
  .header-info {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 24px 0 8px 0;
    margin-bottom: 16px;
    flex-shrink: 0;

    .header-details {
      flex: 1;
      min-width: 0;

      h3 {
        margin: 4px 0 8px 0;
        font-size: 1.7em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.025em;
        line-height: 1.15;
      }

      h3::before {
        content: "VALIDAÇÃO FISCAL · CBS/IBS";
        display: block;
        ${eyebrow};
        margin-bottom: 6px;
        opacity: 0.4;
      }

      .header-meta {
        display: flex;
        gap: 10px;
        align-items: center;
        flex-wrap: wrap;
        font-size: 0.78em;

        .status-tag {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          ${mono};
          font-size: 0.95em;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: ${tokens.brand};

          &::before {
            content: "";
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background: ${tokens.brand};
            box-shadow: 0 0 8px ${tokens.brandRing};
            animation: ${pulseDot} 2.2s ${tokens.easeInOut} infinite;
          }
        }

        .update-tag {
          color: ${({ theme }) => theme.text};
          opacity: 0.4;
          ${mono};
          font-size: 0.9em;
          &::before { content: "· "; opacity: 0.5; }
        }
      }
    }

    .header-actions {
      display: flex;
      gap: 10px;
      flex-wrap: wrap;
      align-self: center;
    }
  }

  /* ========== INPUTS (reset global) ========== */
  .param-input,
  input[type="text"],
  input[type="search"] {
    ${mono};
    width: 100%;
    padding: 8px 12px;
    border-radius: ${tokens.radiusMd};
    border: 1px solid ${({ theme }) => hairline(theme)};
    background: ${({ theme }) => surface(theme, 1)};
    color: ${({ theme }) => theme.text};
    font-size: 0.85em;
    font-weight: 600;
    outline: none;
    transition: border-color ${tokens.durBase} ${tokens.easeOut},
                background ${tokens.durBase} ${tokens.easeOut},
                box-shadow ${tokens.durBase} ${tokens.easeOut};

    &::placeholder { color: ${({ theme }) => theme.text}; opacity: 0.35; font-weight: 500; }

    &:hover {
      border-color: ${({ theme }) => hairline(theme, true)};
      background: ${({ theme }) => surface(theme, 2)};
    }

    &:focus {
      border-color: ${tokens.brand};
      background: ${({ theme }) => surface(theme, 2)};
      box-shadow: 0 0 0 3px ${tokens.brandSoft};
    }
  }

  select {
    ${mono};
    font-size: 0.82em;
    font-weight: 600;
    color: ${({ theme }) => theme.text};
    background: ${({ theme }) => surface(theme, 1)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    padding: 8px 12px;
    outline: none;
    cursor: pointer;

    &:hover { border-color: ${({ theme }) => hairline(theme, true)}; }
    &:focus { border-color: ${tokens.brand}; box-shadow: 0 0 0 3px ${tokens.brandSoft}; }
  }

  textarea {
    ${mono};
    width: 100%;
    font-size: 0.85em;
    font-weight: 500;
    padding: 10px 12px;
    border-radius: ${tokens.radiusMd};
    border: 1px solid ${({ theme }) => hairline(theme)};
    background: ${({ theme }) => surface(theme, 1)};
    color: ${({ theme }) => theme.text};
    outline: none;
    resize: vertical;
    box-sizing: border-box;

    &:focus { border-color: ${tokens.brand}; box-shadow: 0 0 0 3px ${tokens.brandSoft}; }
  }

  /* ========== TABS HEADER ========== */
  .tabs-header {
    display: flex;
    gap: 8px;
    margin-bottom: 14px;
    flex-shrink: 0;
    flex-wrap: wrap;

    .tab {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 7px 14px;
      background: transparent;
      border: 1px solid transparent;
      border-radius: ${tokens.radiusMd};
      color: ${({ theme }) => theme.text};
      opacity: 0.55;
      font-size: 0.78em;
      font-weight: 500;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      ${mono};
      text-transform: uppercase;
      letter-spacing: 0.04em;

      &:hover:not(:disabled) { opacity: 0.85; background: ${({ theme }) => surface(theme, 1)}; }

      &.active {
        opacity: 1;
        background: ${tokens.brandSoft};
        border-color: ${tokens.brandRing};
        color: ${tokens.brand};
      }

      &:disabled { opacity: 0.3; cursor: not-allowed; }

      .tab-badge {
        ${mono};
        margin-left: 2px;
        font-size: 0.85em;
        font-weight: 600;
        padding: 1px 7px;
        border-radius: ${tokens.radiusPill};
        background: ${tokens.warnSoft};
        color: ${tokens.warn};
      }

      svg { font-size: 0.95em; }
    }
  }

  /* ========== BUTTONS ========== */
  .icon-btn {
    ${mono};
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.8em;
    font-weight: 600;
    padding: 10px 16px;
    border-radius: ${tokens.radiusSm};
    border: 1px solid ${({ theme }) => hairline(theme, true)};
    background: ${({ theme }) => surface(theme, 1)};
    color: ${({ theme }) => theme.text};
    cursor: pointer;
    transition: opacity ${tokens.durBase} ${tokens.easeOut};
    white-space: nowrap;

    &:hover { opacity: 0.8; }
    &:disabled { opacity: 0.4; cursor: not-allowed; }

    &.primary {
      border-color: ${tokens.brandRing};
      background: ${tokens.brandSoft};
      color: ${tokens.brand};
    }

    &.danger {
      padding: 6px;
      border-color: ${tokens.dangerSoft};
      background: transparent;
      color: ${tokens.danger};
    }
  }

  /* ========== SECTION TITLE ========== */
  .section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 6px 0 10px 0;

    h4 {
      margin: 0;
      font-size: 0.82em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      opacity: 0.8;
      letter-spacing: -0.01em;
    }

    svg { font-size: 0.9em; opacity: 0.5; color: ${({ theme }) => theme.text}; }
  }

  /* ========== COUNTDOWN BANNER (Painel) ========== */
  .countdown-banner {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    align-items: center;
    ${({ theme }) => cardBase(theme)};
    background: ${tokens.brandSoft};
    border-color: ${tokens.brandRing};
    border-radius: ${tokens.radiusLg};
    padding: 18px 22px;
    margin-bottom: 20px;

    .countdown-number {
      ${mono};
      font-size: 2.2em;
      font-weight: 600;
      color: ${tokens.brand};
      line-height: 1;
    }

    .countdown-text {
      flex: 1 1 320px;
      font-size: 0.86em;
      line-height: 1.5;
      color: ${({ theme }) => theme.text};
      opacity: 0.85;

      strong { font-weight: 600; opacity: 1; }
    }

    .countdown-tag {
      ${mono};
      font-size: 0.76em;
      font-weight: 600;
      color: ${tokens.brand};
      white-space: nowrap;
    }
  }

  /* ========== STEPS LIST (Etapas do fechamento) ========== */
  .steps-card {
    ${({ theme }) => cardBase(theme)};
    padding: 20px;
    margin-bottom: 20px;

    > .steps-title {
      ${eyebrow};
      margin-bottom: 16px;
    }
  }

  .steps-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
    gap: 12px;

    .step-item {
      border-radius: ${tokens.radiusSm};
      padding: 12px 14px;
      background: ${({ theme }) => surface(theme, 1)};
      border: 1px dashed ${({ theme }) => hairline(theme, true)};

      .step-status {
        font-size: 0.72em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        opacity: 0.5;
      }

      .step-label {
        font-size: 0.85em;
        margin-top: 3px;
        color: ${({ theme }) => theme.text};
      }

      &.concluida {
        border: none;
        background: ${tokens.brandSoft};
        .step-status { color: ${tokens.brand}; opacity: 1; }
      }

      &.atual {
        border: none;
        background: #161e25;
        .step-status { color: #afce9b; opacity: 1; }
        .step-label { color: #ffffff; }
      }
    }
  }

  /* ========== KPI CARDS ========== */
  .kpi-cards {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;
    margin-bottom: 20px;
    flex-shrink: 0;

    @media (max-width: 1200px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 500px)  { grid-template-columns: 1fr; }
  }

  .kpi-card {
    ${({ theme }) => cardBase(theme)};
    padding: 16px 18px 14px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 90px;
    position: relative;
    overflow: hidden;

    &:hover {
      border-color: ${({ theme }) => hairline(theme, true)};
      background: ${({ theme }) => surface(theme, 1)};
    }

    .kpi-label {
      font-size: 0.72em;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
    }

    .kpi-value {
      ${mono};
      font-size: 1.5em;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.02em;
      line-height: 1.1;
      margin-top: 8px;
    }

    .kpi-caption {
      ${mono};
      font-size: 0.66em;
      color: ${({ theme }) => theme.text};
      opacity: 0.32;
      margin-top: 4px;
    }
  }

  /* ========== GENERIC DATA TABLE ========== */
  .data-table-card {
    ${({ theme }) => cardBase(theme)};
    overflow: hidden;
    margin-bottom: 20px;

    .data-table-head {
      padding: 16px 20px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;
      border-bottom: 1px solid ${({ theme }) => hairline(theme)};

      .data-table-title { font-size: 0.95em; font-weight: 600; color: ${({ theme }) => theme.text}; }
      .data-table-subtitle { font-size: 0.76em; color: ${({ theme }) => theme.text}; opacity: 0.5; }
    }

    .data-table-scroll { overflow-x: auto; }

    table {
      width: 100%;
      border-collapse: collapse;
    }

    thead th {
      text-align: left;
      font-size: 0.68em;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: ${({ theme }) => theme.text};
      opacity: 0.4;
      padding: 12px 14px;
      border-bottom: 1px solid ${({ theme }) => hairline(theme)};
      white-space: nowrap;
    }

    tbody td {
      font-size: 0.82em;
      color: ${({ theme }) => theme.text};
      padding: 12px 14px;
      border-bottom: 1px solid ${({ theme }) => hairline(theme)};
      white-space: nowrap;
    }

    tfoot td {
      font-size: 0.82em;
      font-weight: 700;
      color: ${({ theme }) => theme.text};
      padding: 12px 14px;
      border-top: 2px solid ${tokens.brandRing};
    }

    tbody tr:last-child td { border-bottom: none; }
    tbody tr:hover td { background: ${({ theme }) => surface(theme, 1)}; }

    .link-chave {
      ${mono};
      font-size: 0.95em;
      background: none;
      border: none;
      padding: 0;
      color: ${tokens.brand};
      cursor: pointer;
      text-decoration: underline;
      text-decoration-color: ${tokens.brandRing};
    }
  }

  /* ========== FILTER BAR (Divergências) ========== */
  .filter-bar {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: flex-end;
    margin-bottom: 16px;

    .filter-field {
      display: flex;
      flex-direction: column;
      gap: 4px;
      font-size: 0.72em;
      color: ${({ theme }) => theme.text};
      opacity: 0.6;

      &.grow { flex: 1 1 240px; }
    }
  }

  /* ========== TYPE CHIPS (resumo por tipo) ========== */
  .type-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 16px;

    .type-chip {
      ${({ theme }) => cardBase(theme)};
      border-radius: ${tokens.radiusPill};
      padding: 6px 14px;
      font-size: 0.78em;
      color: ${({ theme }) => theme.text};
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};

      strong { font-weight: 700; }

      &:hover { background: ${({ theme }) => surface(theme, 1)}; }

      &.active {
        background: ${tokens.brandSoft};
        border-color: ${tokens.brandRing};
        color: ${tokens.brand};
      }
    }
  }

  /* ========== STATUS / SEVERITY PILL ========== */
  .status-pill {
    ${mono};
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 12px;
    border-radius: ${tokens.radiusPill};
    font-size: 0.78em;
    font-weight: 600;
    white-space: nowrap;

    &.success { background: ${tokens.brandSoft}; color: ${tokens.brand}; }
    &.warning  { background: ${tokens.warnSoft}; color: ${tokens.warn}; }
    &.danger   { background: ${tokens.dangerSoft}; color: ${tokens.danger}; }
    &.neutral  { background: ${({ theme }) => surface(theme, 2)}; color: ${({ theme }) => theme.text}; opacity: 0.8; }
    &.dark     { background: #161e25; color: #ffffff; }
  }

  /* ========== EMPTY STATE (Detalhe sem seleção) ========== */
  .empty-tab-state {
    ${({ theme }) => cardBase(theme)};
    padding: 60px 30px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    text-align: center;

    svg { font-size: 2em; opacity: 0.3; color: ${({ theme }) => theme.text}; }

    h4 { margin: 0; font-size: 1em; font-weight: 600; color: ${({ theme }) => theme.text}; }
    p  { margin: 0; font-size: 0.82em; color: ${({ theme }) => theme.text}; opacity: 0.55; max-width: 46ch; }
  }

  /* ========== DETALHE: back link + badges ========== */
  .detalhe-back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.8em;
    font-weight: 600;
    color: ${tokens.brand};
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    margin-bottom: 14px;

    &:hover { opacity: 0.8; }
  }

  .detalhe-header-row {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: center;
    margin-bottom: 6px;

    .detalhe-tipo-tag {
      background: #161e25;
      color: #ffffff;
      border-radius: ${tokens.radiusSm};
      padding: 4px 12px;
      font-size: 0.85em;
      font-weight: 600;
      ${mono};
    }

    h3 { margin: 0; font-size: 1.3em; font-weight: 600; color: ${({ theme }) => theme.text}; letter-spacing: -0.015em; }
  }

  .detalhe-chave {
    ${mono};
    font-size: 0.76em;
    color: ${({ theme }) => theme.text};
    opacity: 0.5;
    margin-bottom: 20px;
    word-break: break-all;
  }

  /* ========== COMPARISON CARDS (AA x ERP x DF-e) ========== */
  .comparison-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 14px;
    margin-bottom: 18px;

    .comparison-card {
      ${({ theme }) => cardBase(theme)};
      padding: 18px;

      .comparison-title { ${eyebrow}; margin-bottom: 10px; }

      .comparison-line {
        font-size: 0.84em;
        line-height: 1.8;
        color: ${({ theme }) => theme.text};
        opacity: 0.8;

        strong { font-weight: 600; opacity: 1; }
      }
    }
  }

  /* ========== DECISION CARD (Detalhe) ========== */
  .decision-layout {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 16px;
    margin-bottom: 20px;
  }

  .decision-card {
    ${({ theme }) => cardBase(theme)};
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;

    .decision-title { font-size: 1em; font-weight: 600; color: ${({ theme }) => theme.text}; }

    label.decision-option {
      display: flex;
      gap: 10px;
      align-items: flex-start;
      font-size: 0.84em;
      color: ${({ theme }) => theme.text};
      cursor: pointer;

      input[type="radio"] { margin-top: 3px; accent-color: ${tokens.brand}; cursor: pointer; }
    }

    .decision-field-label {
      display: flex;
      flex-direction: column;
      gap: 4px;
      font-size: 0.72em;
      color: ${({ theme }) => theme.text};
      opacity: 0.6;
    }

    .decision-actions {
      display: flex;
      gap: 10px;
      flex-wrap: wrap;
    }

    .decision-alcada {
      font-size: 0.74em;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
    }
  }

  .timeline {
    border-top: 1px solid ${({ theme }) => hairline(theme)};
    padding-top: 12px;
    display: flex;
    flex-direction: column;
    gap: 6px;

    .timeline-item {
      font-size: 0.78em;
      line-height: 1.6;
      color: ${({ theme }) => theme.text};
      opacity: 0.65;
      ${mono};
    }
  }

  /* ========== CRÉDITO TRAVADO: ranking + simulação ========== */
  .ranking-layout {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    align-items: flex-start;
    margin-bottom: 20px;

    .ranking-table-wrap { flex: 999 1 560px; min-width: 0; }
    .rad-sim-aside { flex: 1 1 300px; min-width: 0; }
  }

  .rad-sim-aside {
    ${({ theme }) => cardBase(theme)};
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;

    .rad-sim-title { font-size: 1em; font-weight: 600; color: ${({ theme }) => theme.text}; }

    .rad-sim-text {
      font-size: 0.78em;
      color: ${({ theme }) => theme.text};
      opacity: 0.6;
      line-height: 1.5;
    }

    .rad-sim-row {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      font-size: 0.84em;
      color: ${({ theme }) => theme.text};
      padding-top: 10px;
      border-top: 1px solid ${({ theme }) => hairline(theme)};

      strong { ${mono}; font-weight: 600; }

      &:first-of-type { border-top: none; padding-top: 0; }
    }

    .rad-sim-note {
      background: ${({ theme }) => surface(theme, 1)};
      border-radius: ${tokens.radiusSm};
      padding: 12px;
      font-size: 0.74em;
      line-height: 1.5;
      color: ${({ theme }) => theme.text};
      opacity: 0.75;
    }
  }

  .saldo-credor-card {
    ${({ theme }) => cardBase(theme)};
    padding: 20px;
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    align-items: center;
    justify-content: space-between;

    .saldo-credor-text { flex: 1 1 320px; }
    .saldo-credor-title { font-size: 1em; font-weight: 600; color: ${({ theme }) => theme.text}; margin-bottom: 6px; }
    .saldo-credor-desc { font-size: 0.78em; color: ${({ theme }) => theme.text}; opacity: 0.6; line-height: 1.5; }
    .saldo-credor-actions { display: flex; gap: 10px; flex-wrap: wrap; }
  }

  /* ========== INFO BANNER ========== */
  .info-banner {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 14px 18px;
    ${({ theme }) => cardBase(theme)};
    border-radius: ${tokens.radiusMd};
    background: ${tokens.brandSoft};
    border-color: ${tokens.brandRing};
    margin-bottom: 20px;

    .banner-icon {
      flex-shrink: 0;
      width: 30px;
      height: 30px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      background: ${({ theme }) => surface(theme, 0)};
      color: ${tokens.brand};
      svg { font-size: 1em; }
    }

    .banner-text {
      font-size: 0.82em;
      line-height: 1.6;
      color: ${({ theme }) => theme.text};

      strong { font-weight: 600; }
      span { opacity: 0.75; }
    }
  }

  /* ========== CENÁRIOS (Liquidação) ========== */
  .scenario-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 16px;
    margin-bottom: 16px;
  }

  .scenario-card {
    ${({ theme }) => cardBase(theme)};
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;

    .scenario-ref-tag { ${eyebrow}; color: ${tokens.brand}; opacity: 1; }
    .scenario-title { font-size: 0.95em; font-weight: 600; color: ${({ theme }) => theme.text}; }
    .scenario-desc { font-size: 0.78em; color: ${({ theme }) => theme.text}; opacity: 0.6; line-height: 1.5; }

    .scenario-footer {
      border-top: 1px solid ${({ theme }) => hairline(theme)};
      padding-top: 8px;
      display: flex;
      justify-content: space-between;
      font-size: 0.8em;
      color: ${({ theme }) => theme.text};

      strong { ${mono}; }
    }

    &.referencia { border-color: ${tokens.brandRing}; border-width: 2px; }
  }

  /* ========== DOSSIÊ (documento) ========== */
  .dossie-toolbar {
    width: 100%;
    max-width: 880px;
    margin: 0 auto 20px;
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    justify-content: space-between;
    align-items: center;

    .dossie-toolbar-text {
      font-size: 0.82em;
      color: ${({ theme }) => theme.text};
      opacity: 0.6;
      strong { opacity: 1; font-weight: 600; }
    }

    .dossie-toolbar-actions { display: flex; gap: 10px; flex-wrap: wrap; }
  }

  .dossie-doc {
    width: 100%;
    max-width: 880px;
    margin: 0 auto;
    box-sizing: border-box;
    ${({ theme }) => cardBase(theme)};
    padding: 44px;
    display: flex;
    flex-direction: column;
    gap: 26px;
    box-shadow: ${tokens.shadowMd};

    .dossie-header {
      border-bottom: 4px solid ${tokens.brand};
      padding-bottom: 18px;

      .dossie-brand { font-weight: 600; font-size: 0.95em; color: ${({ theme }) => theme.text}; }
      .dossie-brand-sub { ${eyebrow}; margin-top: 2px; }

      h2 {
        margin: 18px 0 6px;
        font-size: 1.5em;
        font-weight: 600;
        letter-spacing: -0.02em;
        color: ${({ theme }) => theme.text};
      }

      .dossie-meta { font-size: 0.8em; color: ${({ theme }) => theme.text}; opacity: 0.6; }
    }

    h3.dossie-section-title {
      margin: 0 0 12px;
      font-size: 1em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
    }

    table { width: 100%; border-collapse: collapse; }

    th.th {
      text-align: left;
      font-size: 0.68em;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: ${({ theme }) => theme.text};
      opacity: 0.45;
      padding: 0 10px 10px;
      border-bottom: 1px solid ${({ theme }) => hairline(theme)};
    }

    td.td {
      font-size: 0.84em;
      color: ${({ theme }) => theme.text};
      padding: 10px;
      border-bottom: 1px solid ${({ theme }) => hairline(theme)};
    }

    tbody tr:last-child td.td { border-bottom: none; }

    .dossie-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
      gap: 12px;

      .dossie-grid-item {
        background: ${({ theme }) => surface(theme, 1)};
        border-radius: ${tokens.radiusSm};
        padding: 14px;

        .dossie-grid-label { font-size: 0.72em; color: ${({ theme }) => theme.text}; opacity: 0.55; }
        .dossie-grid-value { ${mono}; font-size: 1.1em; font-weight: 600; color: ${({ theme }) => theme.text}; margin-top: 4px; }
      }
    }

    ul.dossie-list {
      margin: 0;
      padding-left: 20px;
      font-size: 0.84em;
      line-height: 1.8;
      color: ${({ theme }) => theme.text};
      opacity: 0.85;
    }

    .dossie-parecer {
      border: 1px dashed ${({ theme }) => hairline(theme, true)};
      border-radius: ${tokens.radiusSm};
      padding: 16px;
      font-size: 0.84em;
      color: ${({ theme }) => theme.text};
      opacity: 0.6;
      line-height: 1.6;
    }

    .dossie-signatures {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: 20px;
      padding-top: 8px;

      .dossie-signature {
        border-top: 1px solid ${({ theme }) => theme.text};
        padding-top: 8px;
        font-size: 0.72em;
        color: ${({ theme }) => theme.text};
        opacity: 0.6;
      }
    }

    .dossie-footer {
      font-size: 0.68em;
      color: ${({ theme }) => theme.text};
      opacity: 0.45;
      border-top: 1px solid ${({ theme }) => hairline(theme)};
      padding-top: 12px;
      line-height: 1.5;
    }
  }

  /* ========== FOOTNOTE ========== */
  .table-footnote {
    font-size: 0.74em;
    color: ${({ theme }) => theme.text};
    opacity: 0.45;
    margin-top: 4px;
  }
`;
